import { Link } from "react-router-dom";
import {
  createEmailComposeUrl,
  getContactEmailLabel,
  useContactEmail,
} from "./PublicSiteComponents";
import "./LandingPage.css";

const companyName = "Simplis Software Solutions LLC";
const appName = "Nexus Mobile";
const platformName = "Nexus Game Engine";
const supportEmailToken = "__NEXUS_CONTACT_EMAIL__";
const supportEmail = supportEmailToken;
const siteUrl = "nexusclubengine.com";
const lastUpdated = "May 22, 2026";

const mobilePrivacySections = [
  {
    title: "1. Scope",
    body: [
      `This Mobile Privacy Policy explains how ${companyName} collects, uses, shares, and protects information in connection with ${appName}, the mobile companion app for ${platformName}.`,
      `${appName} helps players, invited users, and authorized staff access club discovery, club details, schedules, RSVP and waitlist tools, reviews, messages, profile information, and dealer workflow tools connected to the ${platformName} platform.`,
    ],
  },
  {
    title: "2. Information You Provide",
    body: [
      "Depending on how you use the app, we may collect account and profile information such as your name, username, phone number, password or password hash, authentication state, player code, role, club memberships, review content, RSVP status, waitlist activity, messages, invite status, and other information you choose to enter or that is created through your use of the app.",
      "For authorized staff or dealer users, the app may process work-related information such as schedule requests, table assignments, session information, game table activity, entries, and profile information connected to the organization that granted access.",
    ],
  },
  {
    title: "3. Location Information",
    body: [
      `${appName} requests location permission only when you use location-based discovery features, such as finding nearby clubs on the Discover page. Location access is used to estimate proximity and improve discovery results.`,
      "You can decline location permission and continue using other app features. You can also manage location permission through your device settings.",
    ],
  },
  {
    title: "4. Device, App, and Security Information",
    body: [
      "We may process basic device, app, and security information needed to operate the app, maintain sessions, troubleshoot errors, prevent abuse, and protect accounts. This may include app version, operating system information, authentication tokens, request metadata, and basic technical logs generated when the app communicates with our servers.",
      "Version 1 of the app does not use push notifications. If push notifications are added later, we will update our disclosures and request any required permissions before using them.",
    ],
  },
  {
    title: "5. How We Use Information",
    body: [
      "We use information to provide, operate, secure, maintain, troubleshoot, support, and improve Nexus Mobile and Nexus Game Engine; create and manage accounts; show club and schedule information; process join, RSVP, waitlist, review, message, profile, and dealer workflow actions; prevent abuse; and respond to support requests.",
      "We may use aggregated or de-identified information to understand app reliability, platform health, feature usage, and product improvements. We do not use aggregated or de-identified information to identify individual users.",
    ],
  },
  {
    title: "6. No In-App Payments or Member Fund Processing",
    body: [
      `${appName} does not process in-app purchases, customer-to-member payments, member-to-member payments, gambling payments, sweepstakes entries, wagering, deposits, withdrawals, cashouts, or transfers of funds.`,
      "Any ledger, wallet, balance, loyalty, promotional, RSVP, waitlist, or similar information shown through Nexus services is customer-entered administrative recordkeeping only and does not represent money held, transmitted, custodied, redeemed, or processed by Simplis.",
    ],
  },
  {
    title: "7. How We Share Information",
    body: [
      "We may share information with service providers that help us operate the app and platform, such as hosting, infrastructure, email, security, support, analytics, and payment processors used for web-based business subscriptions outside the mobile app.",
      "We may disclose information if required by law, legal process, government request, or when necessary to protect the rights, safety, property, security, or integrity of Simplis, customers, users, service providers, or the public.",
    ],
  },
  {
    title: "8. We Do Not Sell or Track for Advertising",
    body: [
      "We do not sell personal information to data brokers. We do not knowingly sell or share personal information for cross-context behavioral advertising.",
      "Nexus Mobile is not designed to track users across apps or websites owned by other companies for advertising purposes.",
    ],
  },
  {
    title: "9. Customer-Controlled Club Data",
    body: [
      "Many records shown in Nexus Mobile are entered, configured, or controlled by customer organizations using Nexus Game Engine. Those organizations are responsible for the accuracy, legality, and appropriateness of the information they enter and for providing any required notices or obtaining required consents.",
      "If your information was entered or controlled by a club or organization, we may direct certain access, correction, export, or deletion requests to that organization because it controls the relevant workspace.",
    ],
  },
  {
    title: "10. Data Retention and Deletion Requests",
    body: [
      `We retain information for as long as reasonably necessary to provide the app and platform, maintain accounts, comply with legal obligations, resolve disputes, enforce agreements, prevent abuse, maintain backups, and support legitimate business purposes.`,
      `You may request access, correction, export, or deletion of information by contacting ${supportEmail}. We may need to verify your identity and authority before fulfilling a request, and some information may be retained where required for legal, security, billing, dispute-resolution, backup, or compliance purposes.`,
    ],
  },
  {
    title: "11. Children's Privacy",
    body: [
      "Nexus Mobile and Nexus Game Engine are intended for users who are at least 18 years old. We do not knowingly collect personal information from anyone under 18. If you believe information from a person under 18 has been entered into the Services, contact us so we can review the issue.",
    ],
  },
  {
    title: "12. Updates and Contact",
    body: [
      `We may update this Mobile Privacy Policy by posting a revised version on ${siteUrl} or within the Services. Continued use of the app after updates are posted means the updated policy applies to future use.`,
      `Privacy questions and data requests may be sent to ${supportEmail}.`,
    ],
  },
];

const supportSections = [
  {
    title: "Account and Login Help",
    body: [
      "If you cannot sign in, confirm that you are using the correct player, staff, or dealer login flow. Player accounts and authorized staff accounts use different access paths inside Nexus Mobile.",
      "If your account belongs to a club or organization, that organization may need to update your access, role, invitation, membership, or staff permissions before we can resolve the issue.",
    ],
  },
  {
    title: "Location and Discovery",
    body: [
      "Location is optional and is used only for nearby club discovery. If nearby results are not working, check that location permission is enabled for Nexus Mobile in your device settings, then reopen the Discover page.",
      "You can still search, join, and view clubs without granting location permission.",
    ],
  },
  {
    title: "Club, Waitlist, RSVP, and Schedule Issues",
    body: [
      "Club details, schedules, RSVP settings, waitlists, dealer schedules, and table activity are managed by the organization using Nexus Game Engine. For club-specific corrections, contact the club or organization first.",
      "If something appears to be a software issue, include your app role, the club or organization name, what screen you were using, and what happened.",
    ],
  },
  {
    title: "Privacy and Data Requests",
    body: [
      `For privacy questions, access requests, correction requests, export requests, or deletion requests, contact ${supportEmail}. We may need to verify your identity and may direct organization-controlled workspace requests to the relevant organization.`,
    ],
  },
  {
    title: "Contact Support",
    body: [
      `For Nexus Mobile support, contact ${supportEmail}. Include your device type, app version if available, account role, and a short description of the problem.`,
    ],
  },
];

function renderText(value, supportEmailLabel) {
  return value.replaceAll(supportEmailToken, supportEmailLabel);
}

function MobilePrivacyPage({ supportEmailLabel }) {
  return (
    <>
      <p className="nexus-eyebrow">{appName}</p>
      <h1>Mobile Privacy Policy</h1>
      <p>Last updated: {lastUpdated}</p>
      <p>
        This policy is specific to Nexus Mobile and is intended for App Store
        review, users, invited users, and authorized organization staff.
      </p>
      {mobilePrivacySections.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => (
            <p key={paragraph}>{renderText(paragraph, supportEmailLabel)}</p>
          ))}
        </section>
      ))}
    </>
  );
}

function MobileSupportPage({ supportEmailLabel, supportEmailAddress }) {
  const composeUrl = createEmailComposeUrl({
    email: supportEmailAddress,
    subject: "Nexus Mobile Support",
  });

  return (
    <>
      <p className="nexus-eyebrow">{appName}</p>
      <h1>Mobile Support</h1>
      <p>Last updated: {lastUpdated}</p>
      <p>
        Support information for Nexus Mobile users, invited users, and
        authorized organization staff.
      </p>
      {supportSections.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => (
            <p key={paragraph}>{renderText(paragraph, supportEmailLabel)}</p>
          ))}
        </section>
      ))}
      <section>
        <h2>Support Link</h2>
        {composeUrl ? (
          <p>
            <a className="nexus-legal__inline-link" href={composeUrl}>
              Contact Nexus Mobile Support
            </a>
          </p>
        ) : (
          <p>Support email is currently loading. Please try again shortly.</p>
        )}
      </section>
    </>
  );
}

export default function MobileLegalPage({ type = "privacy" }) {
  const { contactEmail, isContactEmailLoaded } = useContactEmail();
  const supportEmailLabel =
    contactEmail || (isContactEmailLoaded ? getContactEmailLabel(contactEmail) : "");
  const isSupport = type === "support";

  return (
    <main className="nexus-legal">
      <div className="nexus-legal__shell">
        <Link className="nexus-legal__back" to="/mobile">
          Back to Nexus Mobile
        </Link>
        {isSupport ? (
          <MobileSupportPage
            supportEmailAddress={contactEmail}
            supportEmailLabel={supportEmailLabel}
          />
        ) : (
          <MobilePrivacyPage supportEmailLabel={supportEmailLabel} />
        )}
      </div>
    </main>
  );
}
